import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined'
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined'
import AddOutlinedIcon from '@mui/icons-material/Add'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import LockPersonOutlinedIcon from '@mui/icons-material/LockPersonOutlined'
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { Menu, MenuItem, ListItemIcon, ListItemText, Divider } from '@mui/material'
import FolderFormModal from './FolderFormModal'
import ConfirmModal from './ConfirmModal'
import MoveFolderModal from './MoveFolderModal'
import {
  selectPortfolioFolderTree,
  selectPortfolioTreeLoading,
  selectPortfolioFolderUserCounts,
} from 'ducks/portfolioDocuments/selectors'
import {
  createPortfolioFolderRoutine,
  updatePortfolioFolderRoutine,
  deletePortfolioFolderRoutine,
} from 'ducks/portfolioDocuments/actions'

const Wrap = styled.div`
  width: 290px;
  flex-shrink: 0;
  border-right: 1px solid ${({ theme }) => theme?.colors?.mediumGrey || '#e6e6e6'};
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme?.colors?.lighterGrey || '#fcfdfe'};

  @media (max-width: 768px) {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid ${({ theme }) => theme?.colors?.mediumGrey || '#e6e6e6'};
  }
`
const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 14px 12px;
  border-bottom: 1px solid ${({ theme }) => theme?.colors?.lightGrey || '#f2f2f2'};
`
const HeaderLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme?.colors?.grey?.[800] || '#999'};
`
const NewBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: ${({ theme }) => theme?.colors?.linen || '#fbede9'};
  color: ${({ theme }) => theme?.colors?.primary || '#D24724'};
  border: none;
  border-radius: 7px;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s;
  &:hover { filter: brightness(0.97); }
`
const List = styled.div`
  padding: 8px;
  overflow-y: auto;
  flex: 1;
`
const Row = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 8px 0 ${({ $depth }) => 10 + $depth * 16}px;
  border-radius: 8px;
  cursor: pointer;
  color: ${({ theme }) => theme?.colors?.haiti || '#0C1229'};
  background: ${({ $active, theme }) => ($active ? theme?.colors?.linen || '#fbede9' : 'transparent')};
  transition: background 0.12s;
  &:hover { background: ${({ $active, theme }) => ($active ? theme?.colors?.linen || '#fbede9' : theme?.colors?.lightGrey || '#f2f2f2')}; }
  &:hover .actions { opacity: 1; }
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 7px;
    bottom: 7px;
    width: 3px;
    border-radius: 0 3px 3px 0;
    background: ${({ theme }) => theme?.colors?.primary || '#D24724'};
    opacity: ${({ $active }) => ($active ? 1 : 0)};
  }
`
const Name = styled.span`
  flex: 1;
  font-size: 13.5px;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`
const Lock = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 10px;
  font-weight: 700;
  color: ${({ theme }) => theme?.colors?.secondary || '#33becc'};
`
const FileCount = styled.span`
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme?.colors?.grey?.[800] || '#999'};
  background: ${({ theme }) => theme?.colors?.lightGrey || '#f2f2f2'};
  border-radius: 999px;
  padding: 1px 7px;
`
const Actions = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transition: opacity 0.12s;
`
const IconBtn = styled.button`
  display: inline-flex;
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme?.colors?.grey?.[800] || '#999'};
  padding: 3px;
  border-radius: 6px;
  &:hover { color: ${({ $danger, theme }) => ($danger ? theme?.colors?.error || '#d14925' : theme?.colors?.primary || '#D24724')}; background: rgba(255, 255, 255, 0.7); }
`
const Empty = styled.div`
  padding: 28px 16px;
  text-align: center;
  color: ${({ theme }) => theme?.colors?.grey?.[700] || '#b3b3b3'};
  font-size: 13px;
  line-height: 1.5;
`

const FolderIcon = ({ active }) =>
  active
    ? <FolderOpenOutlinedIcon style={{ fontSize: 18, color: '#D24724' }} />
    : <FolderOutlinedIcon style={{ fontSize: 18, color: '#c9a99b' }} />

function Node({ node, depth, ctx }) {
  const active = ctx.selectedId === node.id
  const count = ctx.userCounts[node.id] || 0
  const [menuEl, setMenuEl] = useState(null)
  const menuOpen = Boolean(menuEl)
  const openMenu = (e) => { e.stopPropagation(); setMenuEl(e.currentTarget) }
  const closeMenu = (e) => { e?.stopPropagation?.(); setMenuEl(null) }
  const run = (fn) => (e) => { e.stopPropagation(); setMenuEl(null); fn() }

  return (
    <>
      <Row
        $depth={depth}
        $active={active}
        onClick={() => ctx.onSelect(node)}
      >
        <FolderIcon active={active} />
        <Name $active={active}>{node.name}</Name>
        {count > 0 && <Lock title={`Restricted to ${count} member${count === 1 ? '' : 's'}`}><LockPersonOutlinedIcon style={{ fontSize: 12 }} />{count}</Lock>}
        {(node.fileCount > 0) && <FileCount title={`${node.fileCount} document${node.fileCount === 1 ? '' : 's'}`}>{node.fileCount}</FileCount>}
        {ctx.canEdit && (
          <Actions className="actions" $open={menuOpen}>
            <IconBtn title="Folder actions" onClick={openMenu}><MoreVertIcon style={{ fontSize: 17 }} /></IconBtn>
          </Actions>
        )}
      </Row>
      {ctx.canEdit && (
        <Menu
          anchorEl={menuEl}
          open={menuOpen}
          onClose={closeMenu}
          onClick={(e) => e.stopPropagation()}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={run(() => ctx.onAddSub(node))}>
            <ListItemIcon><AddOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Add sub-folder</ListItemText>
          </MenuItem>
          <MenuItem onClick={run(() => ctx.onRename(node))}>
            <ListItemIcon><EditOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Rename</ListItemText>
          </MenuItem>
          <MenuItem onClick={run(() => ctx.onMove(node))}>
            <ListItemIcon><DriveFileMoveOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Move folder</ListItemText>
          </MenuItem>
          <MenuItem onClick={run(() => ctx.onManageAccess(node))}>
            <ListItemIcon><LockPersonOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Manage access</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={run(() => ctx.onDelete(node))} sx={{ color: '#d14925' }}>
            <ListItemIcon><DeleteOutlineIcon fontSize="small" sx={{ color: '#d14925' }} /></ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        </Menu>
      )}
      {(node.childFolders || []).map((child) => (
        <Node key={child.id} node={child} depth={depth + 1} ctx={ctx} />
      ))}
    </>
  )
}

export default function PortfolioFolderTree({ portfolioId, canEdit, selectedFolder, onSelect, onManageAccess }) {
  const dispatch = useDispatch()
  const tree = useSelector(selectPortfolioFolderTree)
  const loading = useSelector(selectPortfolioTreeLoading)
  const userCounts = useSelector(selectPortfolioFolderUserCounts)
  const [modal, setModal] = useState(null)

  const closeModal = () => setModal(null)

  const submitCreate = (parentId) => (name) => {
    dispatch(createPortfolioFolderRoutine.trigger({ portfolioId, body: { name, parentFolderId: parentId } }))
    closeModal()
  }
  const submitRename = (folder) => (name) => {
    dispatch(updatePortfolioFolderRoutine.trigger({ portfolioId, folderId: folder.id, body: { name } }))
    closeModal()
  }
  const confirmDelete = (folder) => () => {
    dispatch(deletePortfolioFolderRoutine.trigger({ portfolioId, folderId: folder.id }))
    if (selectedFolder?.id === folder.id) onSelect(null)
    closeModal()
  }

  const ctx = {
    selectedId: selectedFolder?.id,
    canEdit,
    userCounts,
    onSelect,
    onManageAccess,
    onAddSub: (node) => setModal({ type: 'create', parentId: node.id }),
    onRename: (node) => setModal({ type: 'rename', folder: node }),
    onMove: (node) => setModal({ type: 'move', folder: node }),
    onDelete: (node) => setModal({ type: 'delete', folder: node }),
  }

  return (
    <Wrap>
      <Header>
        <HeaderLabel>Folders</HeaderLabel>
        {canEdit && (
          <NewBtn onClick={() => setModal({ type: 'create', parentId: null })}>
            <CreateNewFolderOutlinedIcon style={{ fontSize: 16 }} />
            New
          </NewBtn>
        )}
      </Header>
      <List>
        {loading && <Empty>Loading folders…</Empty>}
        {!loading && (tree || []).map((node) => <Node key={node.id} node={node} depth={0} ctx={ctx} />)}
        {!loading && (!tree || tree.length === 0) && (
          <Empty>No folders yet.<br />{canEdit ? 'Create one to start organising documents.' : 'Nothing has been shared here.'}</Empty>
        )}
      </List>

      {modal?.type === 'create' && (
        <FolderFormModal
          title={modal.parentId ? 'New sub-folder' : 'New folder'}
          confirmLabel="Create"
          onSubmit={submitCreate(modal.parentId)}
          onClose={closeModal}
        />
      )}
      {modal?.type === 'rename' && (
        <FolderFormModal
          title="Rename folder"
          initialValue={modal.folder.name}
          confirmLabel="Rename"
          onSubmit={submitRename(modal.folder)}
          onClose={closeModal}
        />
      )}
      {modal?.type === 'move' && (
        <MoveFolderModal portfolioId={portfolioId} folder={modal.folder} onClose={closeModal} />
      )}
      {modal?.type === 'delete' && (
        <ConfirmModal
          title="Delete folder"
          message={`Delete "${modal.folder.name}" and everything inside it? This cannot be undone.`}
          onConfirm={confirmDelete(modal.folder)}
          onClose={closeModal}
        />
      )}
    </Wrap>
  )
}
